<template>
  <div class="outsourcing-configuration-page">
    <div class="page-hero">
      <div class="page-heading">
        <div class="page-heading-copy">
          <div class="page-eyebrow">
            <span class="eyebrow-dot" />
            Intégrations &amp; flux
          </div>
          <h2 class="page-title">
            Configuration sous-traitance
          </h2>
          <p class="page-subtitle">
            Centralisez les transporteurs, leurs accès et les règles de synchronisation.
          </p>
        </div>
        <div class="page-heading-actions">
          <div class="hero-stat">
            <span>Total</span>
            <strong>{{ companyStats.total }}</strong>
          </div>
          <div class="hero-stat hero-stat-success">
            <span>Actives</span>
            <strong>{{ companyStats.active }}</strong>
          </div>
          <b-button
            variant="outline-primary"
            class="refresh-button"
            :disabled="loading"
            @click="loadInitialData"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="16"
              class="mr-50"
            />
            Actualiser
          </b-button>
        </div>
      </div>
    </div>

    <b-alert
      v-if="error"
      show
      variant="danger"
      class="mt-1"
    >
      {{ error }}
    </b-alert>

    <div class="configuration-layout">
      <b-card class="company-list-card">
        <div class="list-card-heading">
          <div>
            <div class="section-kicker">Catalogue</div>
            <h3 class="list-title">
              Sociétés de sous-traitance
            </h3>
          </div>
          <span class="list-count-badge">
            {{ filteredCompanies.length }} / {{ companyStats.total }}
          </span>
        </div>

        <div class="list-actions">
          <div class="search-wrapper">
            <feather-icon
              icon="SearchIcon"
              size="16"
              class="search-icon"
            />
            <b-form-input
              v-model="search"
              aria-label="Rechercher une société"
              placeholder="Rechercher une société..."
              class="search-input"
            />
            <button
              v-if="search"
              type="button"
              class="clear-search"
              aria-label="Effacer la recherche"
              @click="search = ''"
            >
              <feather-icon
                icon="XIcon"
                size="14"
              />
            </button>
          </div>
          <div class="list-toolbar-buttons">
            <b-button
              variant="outline-primary"
              class="new-button"
              :disabled="loading || saving"
              @click="seedTemplates"
            >
              <feather-icon
                icon="DownloadCloudIcon"
                size="16"
                class="mr-50"
              />
              Initialiser
            </b-button>
            <b-button
              variant="primary"
              class="new-button"
              @click="startCreate"
            >
              <feather-icon
                icon="PlusCircleIcon"
                size="16"
                class="mr-50"
              />
              Nouvelle
            </b-button>
          </div>
        </div>

        <div class="status-filter" role="group" aria-label="Filtrer les sociétés">
          <button
            v-for="filter in statusFilters"
            :key="filter.value"
            type="button"
            class="status-filter-button"
            :class="{ active: statusFilter === filter.value }"
            :aria-pressed="statusFilter === filter.value ? 'true' : 'false'"
            @click="statusFilter = filter.value"
          >
            {{ filter.label }}
            <span>{{ filter.count }}</span>
          </button>
        </div>

        <b-overlay
          :show="loading"
          rounded="sm"
        >
          <div
            v-for="company in filteredCompanies"
            :key="company.id"
            type="button"
            class="company-row"
            :class="{ active: selectedCompany && selectedCompany.id === company.id }"
            @click="selectCompany(company)"
            @keydown.enter="selectCompany(company)"
            role="button"
            tabindex="0"
          >
            <span class="company-avatar">
              <img
                v-if="company.icon"
                :src="company.icon"
                :alt="`Logo ${company.company_label || company.name || ''}`"
              >
              <feather-icon
                v-else
                icon="TruckIcon"
                size="18"
              />
            </span>
            <span class="company-row-main">
              <span class="company-name">{{ company.company_label || 'Sans nom' }}</span>
              <span class="company-meta">
                #{{ company.id }} · {{ company.carrier_type || company.task_name || 'generic' }}
              </span>
            </span>
            <span class="company-row-side">
              <span
                class="company-status"
                :class="{ enabled: company.is_active }"
              >
                <span class="status-dot" />
                {{ company.is_active ? 'Actif' : 'Inactif' }}
              </span>
              <b-button
                variant="link"
                class="company-info-button"
                aria-label="Voir la documentation de l’intégration"
                title="Voir la documentation"
                @click.stop="openCompanyDocumentation(company)"
              >
                <feather-icon
                  icon="InfoIcon"
                  size="15"
                />
              </b-button>
              <feather-icon
                icon="ChevronRightIcon"
                size="16"
                class="company-chevron"
              />
            </span>
          </div>
          <div
            v-if="!filteredCompanies.length && !loading"
            class="empty-list"
          >
            <feather-icon
              icon="SearchIcon"
              size="22"
            />
            <strong>Aucune société trouvée</strong>
            <span>Essayez un autre nom ou réinitialisez la recherche.</span>
            <b-button
              v-if="search || statusFilter !== 'all'"
              variant="link"
              size="sm"
              @click="search = ''; statusFilter = 'all'"
            >
              Réinitialiser les filtres
            </b-button>
          </div>
        </b-overlay>
      </b-card>

      <b-card class="configuration-card">
        <div
          v-if="!form"
          class="empty-state"
        >
          <div class="empty-state-icon">
            <feather-icon
              icon="SlidersIcon"
              size="26"
            />
          </div>
          <h3>Aucune configuration sélectionnée</h3>
          <p>Sélectionnez une société à gauche pour modifier ses paramètres, ou créez une nouvelle intégration.</p>
          <b-button
            variant="primary"
            @click="startCreate"
          >
            <feather-icon
              icon="PlusIcon"
              size="16"
              class="mr-50"
            />
            Créer une configuration
          </b-button>
        </div>

        <div v-else>
          <div class="editor-header">
            <div class="editor-heading-copy">
              <div class="editor-eyebrow">
                <span class="editor-eyebrow-icon">
                  <feather-icon
                    :icon="isCreating ? 'PlusIcon' : 'SettingsIcon'"
                    size="14"
                  />
                </span>
                {{ isCreating ? 'Nouvelle intégration' : 'Configuration transporteur' }}
              </div>
              <h3 class="editor-title">
                {{ isCreating ? 'Nouvelle société' : form.company_label }}
              </h3>
              <small
                v-if="!isCreating"
                class="editor-meta"
              >
                ID {{ form.id }} <span>•</span> Mise à jour {{ formatDate(form.updated_at) }}
              </small>
            </div>
            <div class="editor-actions">
              <b-button
                variant="outline-secondary"
                :disabled="saving"
                @click="resetForm"
              >
                Annuler
              </b-button>
              <b-button
                v-if="!isCreating"
                variant="outline-warning"
                :disabled="saving"
                @click="generateWebhookToken"
              >
                <feather-icon
                  icon="KeyIcon"
                  size="16"
                  class="mr-50"
                />
                Générer token
              </b-button>
              <b-button
                variant="primary"
                :disabled="saving"
                @click="save"
              >
                <b-spinner
                  v-if="saving"
                  small
                  class="mr-50"
                />
                <feather-icon
                  v-else
                  icon="SaveIcon"
                  size="16"
                  class="mr-50"
                />
                Enregistrer
              </b-button>
            </div>
          </div>

          <div
            v-if="!isCreating"
            class="editor-status-strip"
            :class="{ inactive: !form.is_active }"
          >
            <span class="status-strip-icon">
              <feather-icon
                :icon="form.is_active ? 'CheckCircleIcon' : 'PauseCircleIcon'"
                size="17"
              />
            </span>
            <span>
              <strong>{{ form.is_active ? 'Intégration active' : 'Intégration inactive' }}</strong>
              <small>
                {{ form.is_active ? 'Les échanges peuvent être traités par ce transporteur.' : 'Les échanges sont suspendus pour ce transporteur.' }}
              </small>
            </span>
            <b-button
              v-if="!isCreating && currentCompanyTemplate"
              variant="link"
              size="sm"
              class="documentation-link"
              @click="openCompanyDocumentation(form)"
            >
              <feather-icon
                icon="InfoIcon"
                size="14"
                class="mr-25"
              />
              Voir le guide
            </b-button>
          </div>

          <div
            v-if="isCreating"
            class="template-panel"
          >
            <div class="template-header">
              <div>
                <h4>Créer depuis un modèle</h4>
                <p>
                  Choisissez le transporteur, puis remplissez seulement les URLs, tokens et prix du client.
                </p>
              </div>
              <select
                v-model="selectedTemplateId"
                class="form-control template-select"
                @change="onTemplateSelected"
              >
                <option
                  v-for="template in templates"
                  :key="template.id"
                  :value="template.id"
                >
                  {{ template.label }}
                </option>
              </select>
              <b-button
                v-if="selectedTemplate"
                variant="link"
                size="sm"
                class="template-guide-button"
                @click="openTemplateDocumentation"
              >
                <feather-icon
                  icon="InfoIcon"
                  size="14"
                  class="mr-25"
                />
                Voir le guide de remplissage
              </b-button>
            </div>

            <div
              v-if="selectedTemplate"
              class="template-overview"
            >
              <span class="template-overview-icon">
                <feather-icon
                  icon="InfoIcon"
                  size="16"
                />
              </span>
              <span>
                <strong>{{ selectedTemplateDocumentation.summary }}</strong>
                <small>{{ selectedTemplateDocumentation.description }}</small>
              </span>
            </div>

            <div
              v-if="quickFieldDefinitions.length"
              class="quick-field-grid"
            >
              <div
                v-for="field in quickFieldDefinitions"
                :key="field.key"
                class="quick-field"
              >
                <label :for="`quick-${field.key}`">
                  {{ field.label }}
                  <span
                    v-if="isTemplateRequired(field.key)"
                    class="required-mark"
                  >
                    *
                  </span>
                </label>
                <small class="quick-field-key">{{ field.key }}</small>
                <small class="quick-field-help">{{ field.documentation }}</small>

                <textarea
                  v-if="field.type === 'textarea'"
                  :id="`quick-${field.key}`"
                  v-model="form[field.key]"
                  rows="3"
                  class="form-control config-input monospace-input"
                ></textarea>
                <input
                  v-else
                  :id="`quick-${field.key}`"
                  v-model="form[field.key]"
                  :type="field.type === 'number' ? 'number' : 'text'"
                  :step="field.step || null"
                  class="form-control config-input"
                />
              </div>
            </div>
          </div>

          <div
            v-if="!isCreating"
            class="system-summary"
          >
            <span>
              <strong>ID</strong>
              {{ form.id }}
            </span>
            <span>
              <strong>Créé le</strong>
              {{ formatDate(form.created_at) }}
            </span>
            <span>
              <strong>Modifié le</strong>
              {{ formatDate(form.updated_at) }}
            </span>
          </div>

          <b-tabs
            pills
            nav-class="configuration-tabs"
          >
            <b-tab
              v-for="group in fieldGroups"
              :key="group.title"
              :title="group.title"
            >
              <div class="field-grid">
                <div
                  v-for="field in group.fields"
                  :key="field.key"
                  class="field-block"
                  :class="{ wide: field.type === 'textarea' || field.type === 'json' || field.type === 'file' }"
                >
                  <label :for="`outsourcing-${field.key}`">
                    {{ field.label }}
                  </label>
                  <small class="field-key">
                    {{ field.key }}
                  </small>

                  <b-form-checkbox
                    v-if="field.type === 'boolean'"
                    :id="`outsourcing-${field.key}`"
                    v-model="form[field.key]"
                    switch
                  >
                    {{ form[field.key] ? 'Activé' : 'Désactivé' }}
                  </b-form-checkbox>

                  <select
                    v-else-if="field.type === 'select'"
                    :id="`outsourcing-${field.key}`"
                    v-model="form[field.key]"
                    class="form-control config-input"
                  >
                    <option
                      v-for="choice in choices[field.key] || []"
                      :key="choice.value"
                      :value="choice.value"
                    >
                      {{ choice.text }}
                    </option>
                  </select>

                  <div
                    v-else-if="field.type === 'multi'"
                    :id="`outsourcing-${field.key}`"
                    class="checkbox-stack"
                  >
                    <label
                      v-for="choice in choices[field.key] || []"
                      :key="choice.value"
                      class="checkbox-option"
                    >
                      <input
                        v-model="form[field.key]"
                        type="checkbox"
                        :value="choice.value"
                      />
                      <span>{{ choice.text }}</span>
                    </label>
                  </div>

                  <textarea
                    v-else-if="field.type === 'json'"
                    :id="`outsourcing-${field.key}`"
                    v-model="jsonText[field.key]"
                    rows="7"
                    spellcheck="false"
                    class="form-control config-input monospace-input"
                  ></textarea>

                  <textarea
                    v-else-if="field.type === 'textarea'"
                    :id="`outsourcing-${field.key}`"
                    v-model="form[field.key]"
                    rows="5"
                    spellcheck="false"
                    class="form-control config-input monospace-input"
                  ></textarea>

                  <div
                    v-else-if="field.type === 'file'"
                    class="file-field"
                  >
                    <a
                      v-if="form.icon && typeof form.icon === 'string'"
                      :href="form.icon"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Voir l'icône actuelle
                    </a>
                    <input
                      :id="`outsourcing-${field.key}`"
                      type="file"
                      accept="image/*"
                      class="form-control-file config-file-input"
                      @change="onIconFileChange"
                    />
                  </div>

                  <input
                    v-else
                    :id="`outsourcing-${field.key}`"
                    v-model="form[field.key]"
                    :type="field.type === 'number' ? 'number' : 'text'"
                    :step="field.step || null"
                    class="form-control config-input"
                  />

                  <small
                    v-if="field.help"
                    class="field-help"
                  >
                    {{ field.help }}
                  </small>
                </div>
              </div>
            </b-tab>
          </b-tabs>
        </div>
      </b-card>
    </div>

    <b-modal
      ref="integrationDocumentationModal"
      size="lg"
      centered
      hide-footer
      modal-class="integration-documentation-modal"
    >
      <template #modal-title>
        <span class="modal-title-icon">
          <feather-icon
            icon="BookOpenIcon"
            size="17"
          />
        </span>
        Guide de configuration
      </template>

      <div
        v-if="documentationTemplate"
        class="documentation-content"
      >
        <div class="documentation-hero">
          <div class="documentation-hero-icon">
            <feather-icon
              icon="TruckIcon"
              size="23"
            />
          </div>
          <div>
            <span class="documentation-eyebrow">{{ documentationTargetType }}</span>
            <h3>{{ documentationTemplate.label }}</h3>
            <small
              v-if="documentationTarget.company"
              class="documentation-company-name"
            >
              Société configurée : {{ documentationTarget.company.company_label || documentationTarget.company.display_name || documentationTarget.company.name }}
            </small>
            <p>{{ selectedDocumentation.description }}</p>
          </div>
        </div>

        <div class="documentation-note">
          <feather-icon
            icon="InfoIcon"
            size="16"
          />
          <span>{{ selectedDocumentation.summary }}</span>
        </div>

        <div class="documentation-section-heading">
          <div>
            <h4>Champs à renseigner</h4>
            <p>Les champs marqués « Obligatoire » sont nécessaires pour enregistrer l’intégration.</p>
          </div>
          <span class="documentation-progress">
            {{ requiredDocumentationFields.length }} obligatoires
          </span>
        </div>

        <div class="documentation-fields">
          <div
            v-for="field in documentationFields"
            :key="field.key"
            class="documentation-field"
            :class="{ required: field.required }"
          >
            <div class="documentation-field-heading">
              <strong>{{ field.label }}</strong>
              <span :class="field.required ? 'required-badge' : (field.prefilled ? 'prefilled-badge' : 'recommended-badge')">
                {{ field.required ? 'Obligatoire' : (field.prefilled ? 'Prérempli' : 'Recommandé') }}
              </span>
            </div>
            <code>{{ field.key }}</code>
            <p>{{ field.description }}</p>
          </div>
        </div>

        <div class="documentation-section-heading steps-heading">
          <div>
            <h4>Comment procéder</h4>
            <p>Suivez ces étapes pour créer une connexion fiable.</p>
          </div>
        </div>
        <ol class="documentation-steps">
          <li
            v-for="(step, index) in selectedDocumentation.steps"
            :key="step"
          >
            <span>{{ index + 1 }}</span>
            {{ step }}
          </li>
        </ol>

        <div class="documentation-footer">
          <span>
            <feather-icon
              icon="ShieldIcon"
              size="14"
            />
            Les tokens sont confidentiels et ne doivent pas être partagés.
          </span>
          <b-button
            variant="primary"
            @click="$refs.integrationDocumentationModal.hide()"
          >
            J’ai compris
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'

const API_BASE = '/api/companies/outsourcing-company/configuration/'

const FIELD_DOCUMENTATION = {
  name: { label: 'Nom technique', description: 'Identifiant interne stable, sans espace.' },
  display_name: { label: 'Nom affiché', description: 'Nom lisible présenté dans l’application.' },
  base_url: { label: 'URL principale', description: 'Adresse de base fournie par le transporteur pour créer les colis.' },
  api_key: { label: 'Clé API / utilisateur', description: 'Clé API ou identifiant fourni par le transporteur.' },
  token_key: { label: 'Token principal', description: 'Mot de passe ou token utilisé pour authentifier les appels principaux.' },
  token_track: { label: 'Token tracking / mot de passe', description: 'Secret utilisé pour suivre les colis ou l’authentification tracking.' },
  base_url_tracking: { label: 'URL tracking', description: 'Adresse appelée pour récupérer le dernier statut d’un colis.' },
  base_url_history: { label: 'URL historique', description: 'Adresse utilisée pour consulter l’historique des statuts.' },
  base_url_list_colis: { label: 'URL liste colis', description: 'Adresse utilisée pour synchroniser plusieurs colis.' },
  deletion_url: { label: 'URL suppression', description: 'Adresse appelée pour annuler ou supprimer un colis.' },
  webhook_url: { label: 'URL webhook', description: 'Adresse de notre application pour recevoir les notifications.' },
  webhook_token: { label: 'Token webhook', description: 'Secret qui protège les notifications entrantes.' },
  price_delivery: { label: 'Prix livraison', description: 'Montant facturé pour une livraison avec ce transporteur.' },
  price_return: { label: 'Prix retour', description: 'Montant facturé lorsqu’un colis est retourné.' },
  price_exchange: { label: 'Prix échange', description: 'Montant facturé lorsqu’un échange est effectué.' },
}

const TEMPLATE_DOCUMENTATION = {
  zed_delivery_form: {
    summary: 'API Zed Delivery en form-data avec token Bearer.',
    description: 'Renseignez l’URL de création, le token API et l’URL tracking obligatoire. L’URL liste colis est optionnelle.',
    steps: ['Demandez l’URL API, le token et l’URL tracking à Zed Delivery.', 'Renseignez les champs obligatoires.', 'Ajoutez l’URL liste colis si elle est disponible.'],
  },
  saf_express_api: {
    summary: 'API JSON SAF Express, également utilisée par certains partenaires compatibles.',
    description: 'Le nom technique, le nom affiché, l’URL API, la clé API et l’URL tracking sont nécessaires.',
    steps: ['Choisissez un nom technique unique.', 'Copiez la clé API et l’URL fournies par SAF Express.', 'Ajoutez l’URL tracking pour automatiser les statuts.'],
  },
  ciblex: {
    summary: 'API JSON Ciblex avec accès par token.',
    description: 'La connexion Ciblex nécessite l’URL principale, la clé d’accès et l’URL tracking.',
    steps: ['Récupérez l’URL API et le token Ciblex.', 'Renseignez les champs obligatoires.', 'Testez une création et un suivi de colis.'],
  },
  colix_yass_pro: {
    summary: 'API COLIX Yass Pro avec consultation du tracking et de l’historique.',
    description: 'L’URL principale, la clé API et l’URL tracking sont nécessaires. L’URL historique complète le suivi des anomalies.',
    steps: ['Copiez l’URL, la clé API et l’URL tracking fournies par COLIX.', 'Ajoutez l’URL historique si elle est disponible.', 'Enregistrez puis activez après un test.'],
  },
  x_delivery: {
    summary: 'API JSON X-Delivery avec clé d’accès.',
    description: 'Renseignez l’URL principale, la clé API et l’URL tracking X-Delivery pour mettre à jour les statuts.',
    steps: ['Demandez les identifiants et l’URL tracking X-Delivery.', 'Renseignez les champs obligatoires.', 'Enregistrez puis testez une création et un suivi.'],
  },
  bigboss: {
    summary: 'BigBoss utilise la clé API comme nom utilisateur et le token tracking comme mot de passe.',
    description: 'Les accès et l’URL tracking sont indispensables : URL principale, identifiant utilisateur, mot de passe de tracking et endpoint de suivi.',
    steps: ['Demandez à BigBoss l’URL API, l’URL tracking, le nom utilisateur et le mot de passe.', 'Saisissez le nom utilisateur dans « Clé API / utilisateur ».', 'Saisissez le mot de passe dans « Token tracking / mot de passe » puis l’URL tracking.'],
  },
  intigo: {
    summary: 'API Intigo avec clé API et endpoints de suivi.',
    description: 'L’URL principale, la clé API et l’URL tracking sont nécessaires. L’URL historique est optionnelle.',
    steps: ['Récupérez la clé API et l’URL Intigo.', 'Renseignez les champs obligatoires.', 'Complétez les endpoints de suivi.'],
  },
  dropo: {
    summary: 'API JSON Dropo avec token Bearer.',
    description: 'La connexion Dropo nécessite l’URL API, le token d’accès et l’URL tracking.',
    steps: ['Demandez le token d’accès Dropo.', 'Copiez l’URL API et la clé API.', 'Ajoutez l’URL tracking puis activez après validation.'],
  },
  cosmos: {
    summary: 'API JSON Cosmos avec token Bearer.',
    description: 'Renseignez l’URL principale et la clé API Cosmos, puis ajoutez l’URL tracking.',
    steps: ['Récupérez l’URL et le token Cosmos.', 'Renseignez les deux champs obligatoires.', 'Ajoutez l’URL tracking.'],
  },
  aliexpress: {
    summary: 'API Aliexpress Delivery avec token d’accès.',
    description: 'L’URL principale, la clé API et l’URL tracking sont nécessaires pour connecter Aliexpress Delivery.',
    steps: ['Demandez l’URL API, la clé API et l’URL tracking Aliexpress Delivery.', 'Renseignez les champs obligatoires.', 'Testez une création et une mise à jour de statut.'],
  },
  sama_delivery: {
    summary: 'API JSON Sama Delivery avec clé transmise dans le corps de la requête.',
    description: 'La configuration utilise l’URL API, la clé et l’URL tracking fournies par Sama Delivery.',
    steps: ['Récupérez l’URL, la clé et l’URL tracking Sama Delivery.', 'Renseignez les champs obligatoires.', 'Testez une création et une mise à jour de statut.'],
  },
  generic: {
    summary: 'Configuration libre pour un transporteur sans modèle dédié.',
    description: 'Utilisez ce modèle si votre transporteur n’a pas encore de connecteur dédié. L’URL tracking reste obligatoire pour synchroniser les statuts.',
    steps: ['Donnez un nom technique et un nom affiché.', 'Renseignez l’URL principale et l’URL tracking.', 'Ajoutez la clé API et le webhook selon le fournisseur.'],
  },
}

const emptyCompany = () => ({
  name: '',
  display_name: '',
  user: null,
  icon: null,
  is_active: true,
  is_internal_tenant: false,
  carrier_type: '',
  webhook_tenant_name: '',
  base_url: '',
  webhook_url: '',
  auth_method: 'header_bearer',
  integration_mode: 'governorate',
  task_name: 'generic',
  api_key: '',
  webhook_token: '',
  receive_webhooks: true,
  webhook_body_mapping: {},
  token_key: '',
  token_track: '',
  base_url_tracking: '',
  base_url_history: '',
  base_url_list_colis: '',
  subcontractor_payment_details_url: '',
  subcontractor_payment_details_method: 'POST',
  enable_subcontractor_payment_code_import: false,
  after_creation: false,
  after_transfer: false,
  order_creation_mode: 'celery',
  deletion_url: '',
  enabled_extra_statuses: [],
  freeze_delivered_status_from_tracking: false,
  use_zed_pre_etat_temporary_statuses: true,
  intigo_delivered_status_as_temporary: false,
  xdelivery_delivered_status_as_temporary: false,
  bigboss_delivered_status_as_temporary: false,
  price_delivery: 0,
  price_return: 0,
})

export default {
  name: 'OutsourcingConfiguration',
  data() {
    return {
      loading: false,
      saving: false,
      error: '',
      search: '',
      statusFilter: 'all',
      companies: [],
      selectedCompany: null,
      originalForm: null,
      form: null,
      jsonText: {},
      iconFile: null,
      isCreating: false,
      choices: {},
      templates: [],
      selectedTemplateId: '',
      documentationTarget: null,
      quickFieldMap: {
        name: { key: 'name', label: 'Nom technique' },
        display_name: { key: 'display_name', label: 'Nom affiché' },
        base_url: { key: 'base_url', label: 'Base URL' },
        api_key: { key: 'api_key', label: 'API key / utilisateur', type: 'textarea' },
        token_key: { key: 'token_key', label: 'Token key / mot de passe', type: 'textarea' },
        token_track: { key: 'token_track', label: 'Token tracking / mot de passe', type: 'textarea' },
        webhook_url: { key: 'webhook_url', label: 'Webhook URL' },
        webhook_token: { key: 'webhook_token', label: 'Webhook token', type: 'textarea' },
        base_url_tracking: { key: 'base_url_tracking', label: 'Base URL tracking' },
        base_url_history: { key: 'base_url_history', label: 'Base URL historique' },
        base_url_list_colis: { key: 'base_url_list_colis', label: 'Base URL liste colis' },
        subcontractor_payment_details_url: { key: 'subcontractor_payment_details_url', label: 'URL détail payment' },
        deletion_url: { key: 'deletion_url', label: 'URL suppression' },
      },
      fieldGroups: [
        {
          title: 'Identité',
          fields: [
            { key: 'name', label: 'Nom technique' },
            { key: 'display_name', label: 'Nom affiché' },
            { key: 'is_active', label: 'Active', type: 'boolean' },
            { key: 'is_internal_tenant', label: 'Tenant interne', type: 'boolean' },
            { key: 'carrier_type', label: 'Type transporteur' },
          ],
        },
        {
          title: 'API',
          fields: [
            { key: 'base_url', label: 'Base URL' },
            { key: 'base_url_tracking', label: 'Base URL tracking' },
            { key: 'order_creation_mode', label: 'Création colis', type: 'select' },
            { key: 'enable_subcontractor_payment_code_import', label: 'Import code payment', type: 'boolean' },
            { key: 'subcontractor_payment_details_url', label: 'URL détail payment' },
            { key: 'subcontractor_payment_details_method', label: 'Méthode détail payment', type: 'select' },
            { key: 'api_key', label: 'API key', type: 'textarea' },
            { key: 'token_key', label: 'Token key', type: 'textarea' },
            { key: 'token_track', label: 'Token tracking', type: 'textarea' },
          ],
        },
        {
          title: 'Sécurité',
          fields: [
            { key: 'webhook_url', label: 'Webhook URL' },
            { key: 'deletion_url', label: 'URL suppression' },
            { key: 'base_url_history', label: 'Base URL historique' },
            { key: 'base_url_list_colis', label: 'Base URL liste colis' },
            { key: 'webhook_token', label: 'Webhook token', type: 'textarea' },
            { key: 'receive_webhooks', label: 'Recevoir webhooks', type: 'boolean' },
          ],
        },
        {
          title: 'Statuts',
          fields: [
            { key: 'enabled_extra_statuses', label: 'Statuts optionnels activés', type: 'multi' },
            { key: 'freeze_delivered_status_from_tracking', label: 'Ne pas écraser Livré', type: 'boolean' },
            { key: 'use_zed_pre_etat_temporary_statuses', label: 'Zed pre_etat temporaire', type: 'boolean' },
            { key: 'intigo_delivered_status_as_temporary', label: 'Intigo Livré temporaire', type: 'boolean' },
            { key: 'xdelivery_delivered_status_as_temporary', label: 'X-Delivery Livré temporaire', type: 'boolean' },
            { key: 'bigboss_delivered_status_as_temporary', label: 'BigBoss Livré temporaire', type: 'boolean' },
          ],
        },
      ],
    }
  },
  computed: {
    companyStats() {
      const active = this.companies.filter(company => company.is_active).length
      return {
        total: this.companies.length,
        active,
        inactive: this.companies.length - active,
      }
    },
    statusFilters() {
      return [
        { value: 'all', label: 'Toutes', count: this.companyStats.total },
        { value: 'active', label: 'Actives', count: this.companyStats.active },
        { value: 'inactive', label: 'Inactives', count: this.companyStats.inactive },
      ]
    },
    filteredCompanies() {
      const query = this.search.trim().toLowerCase()
      const companies = this.companies.filter(company => {
        const matchesStatus = this.statusFilter === 'all'
          || (this.statusFilter === 'active' && company.is_active)
          || (this.statusFilter === 'inactive' && !company.is_active)
        if (!matchesStatus) return false
        if (!query) return true
        const haystack = [
          company.company_label,
          company.name,
          company.display_name,
          company.carrier_type,
          company.webhook_tenant_name,
        ].join(' ').toLowerCase()
        return haystack.includes(query)
      })

      return companies.sort((first, second) => {
        if (first.is_active !== second.is_active) {
          return first.is_active ? -1 : 1
        }
        return (first.company_label || '').localeCompare(second.company_label || '', 'fr')
      })
    },
    selectedTemplate() {
      return this.templates.find(template => template.id === this.selectedTemplateId) || null
    },
    quickFieldDefinitions() {
      if (!this.selectedTemplate) return []
      const keys = [
        ...this.selectedTemplate.required_fields,
        ...this.selectedTemplate.recommended_fields,
      ]
      return [...new Set(keys)]
        .map(key => this.quickFieldMap[key])
        .filter(Boolean)
        .map(field => ({
          ...field,
          documentation: this.templateFieldDocumentation(field.key, this.selectedTemplate).description,
        }))
    },
    selectedTemplateDocumentation() {
      if (!this.selectedTemplate) return {}
      return TEMPLATE_DOCUMENTATION[this.selectedTemplate.id] || {
        summary: this.selectedTemplate.description,
        description: this.selectedTemplate.description,
        steps: ['Renseignez les champs obligatoires indiqués.', 'Enregistrez la configuration.', 'Effectuez un test de connexion.'],
      }
    },
    documentationTemplate() {
      return this.documentationTarget && this.documentationTarget.template
    },
    currentCompanyTemplate() {
      return !this.isCreating ? this.resolveTemplateForCompany(this.form) : null
    },
    selectedDocumentation() {
      if (!this.documentationTemplate) return {}
      return TEMPLATE_DOCUMENTATION[this.documentationTemplate.id] || {
        summary: this.documentationTemplate.description,
        description: this.documentationTemplate.description,
        steps: ['Renseignez les champs obligatoires indiqués.', 'Enregistrez la configuration.', 'Effectuez un test de connexion.'],
      }
    },
    documentationTargetType() {
      return this.documentationTarget && this.documentationTarget.kind === 'company'
        ? 'Configuration existante'
        : 'Nouvelle intégration'
    },
    documentationFields() {
      if (!this.documentationTemplate) return []
      const required = this.documentationTemplate.required_fields || []
      const recommended = this.documentationTemplate.recommended_fields || []
      const identityFields = ['name', 'display_name']
      const company = this.documentationTarget.company || null
      return [...new Set([...identityFields, ...required, ...recommended])].map(key => ({
        key,
        ...this.templateFieldDocumentation(key, this.documentationTemplate, company),
        required: required.includes(key),
        prefilled: identityFields.includes(key),
      }))
    },
    requiredDocumentationFields() {
      return this.documentationFields.filter(field => field.required)
    },
  },
  created() {
    this.loadInitialData()
  },
  methods: {
    templateFieldDocumentation(key, template, company = null) {
      const documentation = this.fieldDocumentation(key)
      const defaults = (template && template.defaults) || {}
      const templateLabel = (template && template.label) || 'ce transporteur'
      const configuredName = company && (company.name || company.task_name)
      const configuredDisplayName = company && (company.display_name || company.company_label)

      if (key === 'name' && (configuredName || defaults.name)) {
        return {
          ...documentation,
          description: `${documentation.description} Exemple pour ${templateLabel} : ${configuredName || defaults.name}.`,
        }
      }

      if (key === 'display_name' && (configuredDisplayName || defaults.display_name)) {
        return {
          ...documentation,
          description: `${documentation.description} Exemple pour ${templateLabel} : ${configuredDisplayName || defaults.display_name}.`,
        }
      }

      return documentation
    },
    fieldDocumentation(key) {
      return FIELD_DOCUMENTATION[key] || {
        label: key,
        description: 'Valeur fournie par le transporteur pour cette intégration.',
      }
    },
    resolveTemplateForCompany(company) {
      if (!company) return null
      const identifiers = [
        company.task_name,
        company.carrier_type,
        company.name,
        company.company_label,
        company.display_name,
      ]
        .filter(Boolean)
        .map(value => String(value).toLowerCase())
      const matchingTemplate = this.templates.find(template => {
        const defaults = template.defaults || {}
        return identifiers.includes(String(template.id).toLowerCase())
          || identifiers.includes(String(defaults.task_name || '').toLowerCase())
          || identifiers.includes(String(defaults.carrier_type || '').toLowerCase())
          || identifiers.includes(String(defaults.name || '').toLowerCase())
      })
      if (matchingTemplate) return matchingTemplate

      const displayName = company.display_name || company.company_label || company.name || 'Transporteur'
      const technicalName = company.name || company.task_name || `company_${company.id || 'custom'}`
      return {
        id: `custom-${technicalName}`,
        label: displayName,
        description: `Configuration de ${displayName}. Les champs indiqués correspondent à cette société configurée.`,
        required_fields: ['base_url', 'api_key', 'base_url_tracking'],
        recommended_fields: ['token_key', 'token_track', 'base_url_history', 'base_url_list_colis', 'deletion_url'],
        defaults: {
          name: technicalName,
          display_name: displayName,
        },
      }
    },
    showDocumentation(target, kind) {
      const template = kind === 'company'
        ? this.resolveTemplateForCompany(target)
        : target
      if (!template) return
      this.documentationTarget = {
        kind,
        template,
        company: kind === 'company' ? target : null,
      }
      this.$nextTick(() => {
        this.$refs.integrationDocumentationModal.show()
      })
    },
    openCompanyDocumentation(company) {
      this.showDocumentation(company, 'company')
    },
    openTemplateDocumentation() {
      this.showDocumentation(this.selectedTemplate, 'template')
    },
    onTemplateSelected() {
      this.applySelectedTemplate()
      this.openTemplateDocumentation()
    },
    async loadInitialData() {
      this.loading = true
      this.error = ''
      try {
        const [metadataResponse, companiesResponse] = await Promise.all([
          axios.get(`${API_BASE}metadata/`),
          axios.get(API_BASE),
        ])
        this.choices = metadataResponse.data.choices || {}
        this.templates = metadataResponse.data.templates || []
        if (!this.selectedTemplateId && this.templates.length) {
          this.selectedTemplateId = this.templates[0].id
        }
        this.companies = Array.isArray(companiesResponse.data)
          ? companiesResponse.data
          : companiesResponse.data.results || []
        if (this.companies.length && !this.form) {
          this.selectCompany(this.companies[0])
        }
      } catch (error) {
        this.error = this.extractErrorMessage(error, 'Impossible de charger les configurations.')
      } finally {
        this.loading = false
      }
    },
    cloneCompany(company) {
      return JSON.parse(JSON.stringify(company || emptyCompany()))
    },
    syncJsonText() {
      this.jsonText = {
        webhook_body_mapping: JSON.stringify(this.form.webhook_body_mapping || {}, null, 2),
      }
    },
    selectCompany(company) {
      this.isCreating = false
      this.iconFile = null
      this.documentationTarget = null
      this.selectedCompany = company
      this.form = this.cloneCompany(company)
      this.originalForm = this.cloneCompany(company)
      this.syncJsonText()
    },
    startCreate() {
      this.isCreating = true
      this.selectedCompany = null
      this.iconFile = null
      this.documentationTarget = null
      this.form = emptyCompany()
      if (!this.selectedTemplateId && this.templates.length) {
        this.selectedTemplateId = this.templates[0].id
      }
      this.applySelectedTemplate()
      this.originalForm = this.cloneCompany(this.form)
      this.syncJsonText()
    },
    applySelectedTemplate() {
      if (!this.form || !this.selectedTemplate) return
      const preservedValues = {
        base_url: this.form.base_url,
        api_key: this.form.api_key,
        token_key: this.form.token_key,
        token_track: this.form.token_track,
        webhook_url: this.form.webhook_url,
        webhook_token: this.form.webhook_token,
        webhook_tenant_name: this.form.webhook_tenant_name,
        base_url_tracking: this.form.base_url_tracking,
        base_url_history: this.form.base_url_history,
        base_url_list_colis: this.form.base_url_list_colis,
        subcontractor_payment_details_url: this.form.subcontractor_payment_details_url,
        deletion_url: this.form.deletion_url,
        price_delivery: this.form.price_delivery,
        price_return: this.form.price_return,
      }
      this.form = {
        ...this.form,
        ...(this.selectedTemplate.defaults || {}),
        ...preservedValues,
      }
      this.originalForm = this.cloneCompany(this.form)
      this.syncJsonText()
    },
    isTemplateRequired(key) {
      return Boolean(
        this.selectedTemplate
        && this.selectedTemplate.required_fields
        && this.selectedTemplate.required_fields.includes(key),
      )
    },
    resetForm() {
      this.iconFile = null
      this.form = this.cloneCompany(this.originalForm)
      this.syncJsonText()
    },
    onIconFileChange(event) {
      const files = event.target.files || []
      this.iconFile = files.length ? files[0] : null
    },
    formatDate(value) {
      if (!value) return '-'
      return new Date(value).toLocaleString('fr-FR')
    },
    normalizePayload() {
      const payload = this.cloneCompany(this.form)
      delete payload.id
      delete payload.company_label
      delete payload.created_at
      delete payload.updated_at

      try {
        payload.webhook_body_mapping = JSON.parse(this.jsonText.webhook_body_mapping || '{}')
      } catch (error) {
        throw new Error('Le mapping body webhook doit être un JSON valide.')
      }

      payload.user = payload.user === '' ? null : payload.user
      payload.price_delivery = Number(payload.price_delivery || 0)
      payload.price_return = Number(payload.price_return || 0)
      if (!this.iconFile) delete payload.icon
      return payload
    },
    buildRequestBody(payload) {
      if (!this.iconFile) return payload
      const formData = new FormData()
      Object.keys(payload).forEach(key => {
        const value = payload[key]
        if (Array.isArray(value) || (value && typeof value === 'object')) {
          formData.append(key, JSON.stringify(value))
        } else if (value !== undefined && value !== null) {
          formData.append(key, value)
        }
      })
      formData.append('icon', this.iconFile)
      return formData
    },
    requestConfig(body) {
      if (body instanceof FormData) {
        return { headers: { 'Content-Type': 'multipart/form-data' } }
      }
      return undefined
    },
    updateCompanyInList(company) {
      const index = this.companies.findIndex(row => row.id === company.id)
      if (index === -1) {
        this.companies.unshift(company)
      } else {
        this.$set(this.companies, index, company)
      }
      this.selectCompany(company)
    },
    async seedTemplates() {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Créer les configurations manquantes depuis le catalogue transporteurs ? '
        + 'Elles seront créées inactives, puis vous pourrez remplir les URLs/tokens.',
        {
          title: 'Initialiser le catalogue',
          okTitle: 'Initialiser',
          cancelTitle: 'Annuler',
          okVariant: 'primary',
          cancelVariant: 'outline-secondary',
        },
      )
      if (!confirmed) return

      this.saving = true
      try {
        const { data } = await axios.post(`${API_BASE}seed-templates/`)
        this.companies = data.companies || []
        if (this.companies.length) {
          this.selectCompany(this.companies[0])
        }
        this.$bvToast.toast(data.message || 'Catalogue initialisé.', {
          title: 'Sous-traitance',
          variant: 'success',
          solid: true,
        })
      } catch (error) {
        this.$bvToast.toast(this.extractErrorMessage(error, 'Impossible d’initialiser le catalogue.'), {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.saving = false
      }
    },
    async save() {
      this.saving = true
      try {
        const payload = this.normalizePayload()
        const body = this.buildRequestBody(payload)
        const config = this.requestConfig(body)
        let response

        if (this.isCreating && this.selectedTemplateId && !(body instanceof FormData)) {
          response = await axios.post(`${API_BASE}from-template/`, {
            template_id: this.selectedTemplateId,
            configuration: payload,
          })
        } else if (this.isCreating) {
          response = await axios.post(API_BASE, body, config)
        } else {
          response = await axios.patch(`${API_BASE}${this.form.id}/`, body, config)
        }

        this.updateCompanyInList(response.data)
        this.$bvToast.toast('Configuration enregistrée avec succès.', {
          title: 'Sous-traitance',
          variant: 'success',
          solid: true,
        })
      } catch (error) {
        this.$bvToast.toast(this.extractErrorMessage(error, error.message || 'Impossible d’enregistrer.'), {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.saving = false
      }
    },
    async generateWebhookToken() {
      if (!this.form || !this.form.id) return
      this.saving = true
      try {
        const { data } = await axios.post(`${API_BASE}${this.form.id}/generate-webhook-token/`)
        this.updateCompanyInList(data)
        this.$bvToast.toast('Webhook token généré.', {
          title: 'Sous-traitance',
          variant: 'success',
          solid: true,
        })
      } catch (error) {
        this.$bvToast.toast(this.extractErrorMessage(error, 'Impossible de générer le token.'), {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.saving = false
      }
    },
    extractErrorMessage(error, fallback) {
      const data = error.response && error.response.data
      if (!data) return fallback
      if (typeof data === 'string') return data
      if (data.detail) return data.detail
      if (data.error) return data.error
      const firstKey = Object.keys(data)[0]
      if (!firstKey) return fallback
      const value = data[firstKey]
      if (Array.isArray(value)) return `${firstKey}: ${value.join(', ')}`
      return `${firstKey}: ${value}`
    },
  },
}
</script>

<style scoped>
.outsourcing-configuration-page {
  padding-bottom: 2rem;
}

.page-heading,
.editor-header,
.editor-actions,
.list-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.page-title,
.editor-title {
  margin-bottom: 0.35rem;
  font-weight: 700;
}

.page-title {
  font-size: 1.5rem;
}

.editor-title {
  font-size: 1.25rem;
}

.page-subtitle {
  margin-bottom: 0;
  color: #6e6b7b;
}

.configuration-layout {
  display: grid;
  grid-template-columns: minmax(260px, 320px) minmax(0, 1fr);
  gap: 1rem;
  margin-top: 1.25rem;
}

.company-list-card,
.configuration-card {
  border-radius: 8px;
}

.new-button {
  white-space: nowrap;
}

.company-row {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  padding: 0.85rem;
  margin-top: 0.65rem;
  text-align: left;
  color: #5e5873;
  background: #fff;
  border: 1px solid #ebe9f1;
  border-radius: 8px;
}

.company-row.active {
  color: #d00000;
  border-color: #d00000;
  background: rgba(208, 0, 0, 0.04);
}

.company-row-main {
  min-width: 0;
}

.company-name,
.company-meta {
  display: block;
}

.company-name {
  font-weight: 700;
}

.company-status {
  flex: 0 0 auto;
  padding: 0.18rem 0.45rem;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1;
  color: #6e6b7b;
  background: #f1f1f3;
  border: 1px solid #e2e0e8;
  border-radius: 999px;
}

.company-status.enabled {
  color: #0f5132;
  background: #dff7e8;
  border-color: #9ee0b4;
}

.company-meta,
.field-help,
.empty-list {
  color: #6e6b7b;
}

.empty-state,
.empty-list {
  padding: 1rem;
  text-align: center;
}

.editor-actions {
  flex-wrap: wrap;
}

.template-panel {
  margin-top: 1rem;
  padding: 1rem;
  background: #fbfbfc;
  border: 1px solid #ebe9f1;
  border-radius: 8px;
}

.template-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.template-header h4 {
  margin-bottom: 0.25rem;
  font-size: 1rem;
  font-weight: 700;
}

.template-header p,
.template-description {
  margin-bottom: 0;
  color: #6e6b7b;
}

.template-select {
  max-width: 320px;
}

.template-description {
  margin-top: 0.75rem;
}

.template-guide-button {
  align-self: flex-end;
  margin-top: 0.35rem;
  padding-right: 0;
  padding-left: 0;
  color: #7367f0;
  white-space: nowrap;
}

.template-guide-button:hover,
.template-guide-button:focus {
  color: #5145c9;
  text-decoration: none;
}

.template-overview {
  display: flex;
  gap: 0.65rem;
  align-items: flex-start;
  margin-top: 0.85rem;
  padding: 0.7rem 0.8rem;
  color: #5145c9;
  background: #f4f2ff;
  border: 1px solid #e3defd;
  border-radius: 8px;
}

.template-overview-icon {
  display: inline-flex;
  padding-top: 0.1rem;
}

.template-overview strong,
.template-overview small {
  display: block;
}

.template-overview strong {
  margin-bottom: 0.15rem;
  color: #403866;
  font-size: 0.8rem;
}

.template-overview small {
  color: #6e6b7b;
  line-height: 1.45;
}

.quick-field-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.quick-field label {
  display: block;
  margin-bottom: 0.15rem;
  font-weight: 600;
}

.quick-field small {
  display: block;
  margin-bottom: 0.4rem;
  color: #6e6b7b;
  font-family: monospace;
}

.quick-field-key {
  margin-bottom: 0.15rem !important;
}

.quick-field-help {
  min-height: 2.1rem;
  margin-bottom: 0.45rem !important;
  color: #817d90 !important;
  font-family: inherit !important;
  font-size: 0.72rem;
  line-height: 1.35;
}

.required-mark {
  color: #d00000;
}

.system-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1rem;
  padding: 0.75rem;
  color: #5e5873;
  background: #f8f8f8;
  border: 1px solid #ebe9f1;
  border-radius: 8px;
}

.system-summary span {
  display: inline-flex;
  gap: 0.35rem;
  align-items: center;
}

.configuration-tabs {
  margin-top: 1.25rem;
}

.field-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  padding-top: 1.25rem;
}

.field-block {
  min-width: 0;
}

.field-block.wide {
  grid-column: 1 / -1;
}

.field-block label {
  display: block;
  margin-bottom: 0.15rem;
  font-weight: 600;
}

.field-key {
  display: block;
  margin-bottom: 0.4rem;
  color: #6e6b7b;
  font-family: monospace;
}

.field-help {
  display: block;
  margin-top: 0.35rem;
}

.config-input {
  display: block;
  width: 100%;
  min-height: 2.714rem;
  color: #5e5873;
  background-color: #fff;
  border: 1px solid #d8d6de;
  border-radius: 0.357rem;
}

.config-input:focus {
  border-color: #7367f0;
  box-shadow: 0 3px 10px 0 rgba(115, 103, 240, 0.12);
}

.config-file-input {
  display: block;
  width: 100%;
  padding: 0.5rem 0;
  color: #5e5873;
}

.monospace-input {
  font-family: monospace;
}

.checkbox-stack {
  padding: 0.75rem;
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  max-height: 280px;
  overflow: auto;
}

.checkbox-option {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
  margin-bottom: 0.5rem;
  font-weight: 400;
}

.checkbox-option:last-child {
  margin-bottom: 0;
}

.checkbox-option input {
  margin-top: 0.2rem;
}

.file-field {
  display: grid;
  gap: 0.65rem;
}

/* Refined workspace layout */
.outsourcing-configuration-page {
  color: #2f2b3d;
}

.page-hero {
  padding: 1.15rem 1.35rem;
  background: linear-gradient(135deg, #f7f5ff 0%, #ffffff 72%);
  border: 1px solid #ebe8fb;
  border-radius: 14px;
}

.page-heading {
  align-items: center;
}

.page-heading-copy {
  min-width: 0;
}

.page-eyebrow,
.editor-eyebrow,
.section-kicker {
  display: flex;
  gap: 0.45rem;
  align-items: center;
  margin-bottom: 0.4rem;
  color: #7367f0;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  line-height: 1.2;
  text-transform: uppercase;
}

.eyebrow-dot {
  width: 0.45rem;
  height: 0.45rem;
  background: #7367f0;
  border-radius: 50%;
  box-shadow: 0 0 0 4px rgba(115, 103, 240, 0.12);
}

.page-title {
  color: #2f2b3d;
  font-size: 1.65rem;
  letter-spacing: -0.02em;
}

.page-subtitle {
  max-width: 650px;
  line-height: 1.55;
}

.page-heading-actions {
  display: flex;
  gap: 0.7rem;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.hero-stat {
  display: flex;
  min-width: 4.4rem;
  padding: 0.5rem 0.7rem;
  flex-direction: column;
  align-items: flex-start;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid #e7e4f5;
  border-radius: 9px;
}

.hero-stat span {
  color: #8f8b9f;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
}

.hero-stat strong {
  color: #403866;
  font-size: 1.1rem;
  line-height: 1.25;
}

.hero-stat-success strong {
  color: #168a52;
}

.refresh-button {
  height: 2.65rem;
}

.configuration-layout {
  align-items: start;
  gap: 1.25rem;
  margin-top: 1.25rem;
}

.company-list-card,
.configuration-card {
  border: 1px solid #ebe9f1;
  box-shadow: 0 8px 24px rgba(47, 43, 61, 0.06);
}

.company-list-card {
  position: sticky;
  top: 1.25rem;
  max-height: calc(100vh - 2rem);
  overflow: hidden;
}

.company-list-card > .card-body {
  overflow-y: auto;
}

.list-card-heading {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
  justify-content: space-between;
}

.section-kicker {
  margin-bottom: 0.2rem;
  color: #9691a9;
  font-size: 0.65rem;
}

.list-title {
  margin-bottom: 0;
  color: #2f2b3d;
  font-size: 1rem;
  font-weight: 700;
}

.list-count-badge {
  padding: 0.28rem 0.5rem;
  color: #7367f0;
  background: #f1efff;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  white-space: nowrap;
}

.list-actions {
  display: grid;
  gap: 0.65rem;
  margin-top: 1rem;
}

.list-toolbar-buttons {
  display: flex;
  gap: 0.5rem;
}

.list-toolbar-buttons .new-button {
  flex: 1;
}

.search-wrapper {
  position: relative;
}

.search-input {
  height: 2.55rem;
  padding-right: 2.2rem;
  padding-left: 2.15rem;
  border-color: #e3e0eb;
}

.search-input:focus {
  border-color: #7367f0;
  box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.1);
}

.search-icon,
.clear-search {
  position: absolute;
  top: 50%;
  z-index: 2;
  color: #9b97aa;
  transform: translateY(-50%);
}

.search-icon {
  left: 0.75rem;
}

.clear-search {
  right: 0.55rem;
  display: inline-flex;
  padding: 0.2rem;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.clear-search:hover {
  color: #7367f0;
}

.status-filter {
  display: flex;
  gap: 0.25rem;
  margin: 0.9rem 0 0.3rem;
  padding: 0.2rem;
  background: #f7f6fa;
  border-radius: 8px;
}

.status-filter-button {
  flex: 1;
  padding: 0.38rem 0.35rem;
  color: #777287;
  background: transparent;
  border: 0;
  border-radius: 6px;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
}

.status-filter-button span {
  display: inline-flex;
  min-width: 1.15rem;
  height: 1.15rem;
  margin-left: 0.15rem;
  align-items: center;
  justify-content: center;
  color: #9a96a9;
  background: #ebe9f1;
  border-radius: 999px;
  font-size: 0.64rem;
}

.status-filter-button.active {
  color: #7367f0;
  background: #fff;
  box-shadow: 0 2px 7px rgba(47, 43, 61, 0.08);
}

.status-filter-button.active span {
  color: #fff;
  background: #7367f0;
}

.company-row {
  align-items: center;
  min-height: 4.25rem;
  padding: 0.7rem 0.65rem;
  margin-top: 0.5rem;
  transition: border-color 0.18s ease, background-color 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease;
}

.company-row:hover {
  border-color: #c9c3f7;
  background: #faf9ff;
  box-shadow: 0 4px 12px rgba(115, 103, 240, 0.08);
  transform: translateY(-1px);
}

.company-row.active {
  color: #5e5873;
  border-color: #7367f0;
  background: linear-gradient(90deg, #f5f3ff 0%, #fff 100%);
  box-shadow: 0 4px 12px rgba(115, 103, 240, 0.1);
}

.company-avatar {
  display: inline-flex;
  width: 2.35rem;
  height: 2.35rem;
  margin-right: 0.1rem;
  flex: 0 0 2.35rem;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: #7367f0;
  background: #f0eeff;
  border-radius: 9px;
}

.company-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.company-row-main {
  padding-right: 0.4rem;
}

.company-name {
  overflow: hidden;
  color: #3f3a50;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.company-meta {
  margin-top: 0.18rem;
  font-size: 0.7rem;
}

.company-row-side {
  display: flex;
  gap: 0.35rem;
  align-items: center;
  flex: 0 0 auto;
}

.company-status {
  display: inline-flex;
  gap: 0.3rem;
  align-items: center;
  padding: 0.25rem 0.42rem;
}

.status-dot {
  width: 0.38rem;
  height: 0.38rem;
  background: #9b97aa;
  border-radius: 50%;
}

.company-status.enabled .status-dot {
  background: #20a464;
}

.company-chevron {
  color: #b0adba;
}

.company-info-button {
  display: inline-flex;
  padding: 0.2rem;
  color: #9b97aa;
  border-radius: 5px;
}

.company-info-button:hover,
.company-info-button:focus {
  color: #7367f0;
  background: #f0eeff;
}

.empty-list {
  display: flex;
  min-height: 10rem;
  gap: 0.45rem;
  align-items: center;
  justify-content: center;
  color: #9b97aa;
  flex-direction: column;
}

.empty-list strong {
  color: #5e5873;
  font-size: 0.9rem;
}

.empty-list span {
  font-size: 0.75rem;
}

.empty-state {
  display: flex;
  min-height: 27rem;
  gap: 0.7rem;
  align-items: center;
  justify-content: center;
  color: #8f8b9f;
  flex-direction: column;
  text-align: center;
}

.empty-state-icon {
  display: inline-flex;
  width: 3.5rem;
  height: 3.5rem;
  align-items: center;
  justify-content: center;
  color: #7367f0;
  background: #f0eeff;
  border-radius: 14px;
}

.empty-state h3 {
  margin: 0.35rem 0 0;
  color: #3f3a50;
  font-size: 1.05rem;
}

.empty-state p {
  max-width: 25rem;
  margin-bottom: 0.55rem;
  line-height: 1.5;
}

.configuration-card {
  min-height: 32rem;
}

.editor-header {
  align-items: center;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0eef4;
}

.editor-heading-copy {
  min-width: 0;
}

.editor-eyebrow {
  margin-bottom: 0.3rem;
  color: #9691a9;
}

.editor-eyebrow-icon {
  display: inline-flex;
  width: 1.45rem;
  height: 1.45rem;
  align-items: center;
  justify-content: center;
  color: #7367f0;
  background: #f0eeff;
  border-radius: 5px;
}

.editor-title {
  max-width: 42rem;
  overflow: hidden;
  color: #2f2b3d;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.editor-meta {
  color: #9691a9;
}

.editor-meta span {
  padding: 0 0.25rem;
  color: #c2bfca;
}

.editor-status-strip {
  display: flex;
  gap: 0.65rem;
  align-items: center;
  margin-top: 1rem;
  padding: 0.65rem 0.75rem;
  color: #176c43;
  background: #ecfbf3;
  border: 1px solid #c8f0da;
  border-radius: 9px;
}

.editor-status-strip.inactive {
  color: #8a5a08;
  background: #fff8e8;
  border-color: #f7dfaa;
}

.status-strip-icon {
  display: inline-flex;
}

.editor-status-strip strong,
.editor-status-strip small {
  display: block;
}

.editor-status-strip strong {
  font-size: 0.78rem;
}

.editor-status-strip small {
  margin-top: 0.1rem;
  opacity: 0.82;
}

.documentation-link {
  margin-left: auto;
  color: #7367f0;
  white-space: nowrap;
}

.documentation-link:hover,
.documentation-link:focus {
  color: #5145c9;
  text-decoration: none;
}

.template-panel {
  background: linear-gradient(135deg, #faf9ff 0%, #fff 100%);
  border-color: #e7e3fc;
}

.system-summary {
  color: #625e70;
  background: #faf9fc;
}

.configuration-tabs {
  padding-bottom: 0.2rem;
  border-bottom: 1px solid #ebe9f1;
}

.configuration-tabs .nav-link {
  color: #777287;
  font-weight: 600;
}

.configuration-tabs .nav-link.active {
  color: #fff;
  background: #7367f0;
  box-shadow: 0 4px 10px rgba(115, 103, 240, 0.2);
}

.modal-title-icon {
  display: inline-flex;
  width: 1.7rem;
  height: 1.7rem;
  margin-right: 0.4rem;
  align-items: center;
  justify-content: center;
  color: #7367f0;
  background: #f0eeff;
  border-radius: 6px;
  vertical-align: middle;
}

.documentation-content {
  color: #5e5873;
}

.documentation-hero {
  display: flex;
  gap: 0.85rem;
  align-items: flex-start;
  padding: 1rem;
  background: linear-gradient(135deg, #f5f3ff 0%, #fff 100%);
  border: 1px solid #e5e1fb;
  border-radius: 10px;
}

.documentation-hero-icon {
  display: inline-flex;
  width: 2.8rem;
  height: 2.8rem;
  flex: 0 0 2.8rem;
  align-items: center;
  justify-content: center;
  color: #7367f0;
  background: #fff;
  border: 1px solid #ded8fa;
  border-radius: 9px;
}

.documentation-eyebrow {
  display: block;
  margin-bottom: 0.25rem;
  color: #7367f0;
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.documentation-hero h3 {
  margin-bottom: 0.2rem;
  color: #2f2b3d;
  font-size: 1.15rem;
  font-weight: 700;
}

.documentation-company-name {
  display: block;
  margin-bottom: 0.25rem;
  color: #7367f0;
  font-size: 0.76rem;
  font-weight: 600;
}

.documentation-hero p {
  margin-bottom: 0;
  line-height: 1.5;
}

.documentation-note {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
  margin-top: 1rem;
  padding: 0.7rem 0.8rem;
  color: #625e70;
  background: #faf9fc;
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  font-size: 0.8rem;
  line-height: 1.45;
}

.documentation-note .feather {
  flex: 0 0 auto;
  color: #7367f0;
}

.documentation-section-heading {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  justify-content: space-between;
  margin-top: 1.25rem;
}

.documentation-section-heading h4 {
  margin-bottom: 0.2rem;
  color: #2f2b3d;
  font-size: 0.95rem;
  font-weight: 700;
}

.documentation-section-heading p {
  margin-bottom: 0;
  color: #8f8b9f;
  font-size: 0.78rem;
}

.documentation-progress {
  padding: 0.28rem 0.5rem;
  color: #7367f0;
  background: #f0eeff;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  white-space: nowrap;
}

.documentation-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.65rem;
  margin-top: 0.8rem;
}

.documentation-field {
  padding: 0.75rem;
  background: #faf9fc;
  border: 1px solid #ebe9f1;
  border-left: 3px solid #d8d6de;
  border-radius: 8px;
}

.documentation-field.required {
  background: #fffdf8;
  border-left-color: #f0ad26;
}

.documentation-field-heading {
  display: flex;
  gap: 0.4rem;
  align-items: flex-start;
  justify-content: space-between;
}

.documentation-field-heading strong {
  color: #3f3a50;
  font-size: 0.8rem;
}

.documentation-field code {
  display: block;
  margin-top: 0.2rem;
  color: #8f8b9f;
  font-size: 0.68rem;
}

.documentation-field p {
  margin: 0.45rem 0 0;
  color: #777287;
  font-size: 0.75rem;
  line-height: 1.45;
}

.required-badge,
.recommended-badge,
.prefilled-badge {
  padding: 0.18rem 0.35rem;
  border-radius: 999px;
  font-size: 0.62rem;
  font-weight: 700;
  white-space: nowrap;
}

.required-badge {
  color: #9a6400;
  background: #fff0c9;
}

.recommended-badge {
  color: #4d48a8;
  background: #eceaff;
}

.prefilled-badge {
  color: #176c43;
  background: #e4f7ec;
}

.steps-heading {
  margin-top: 1.35rem;
}

.documentation-steps {
  display: grid;
  gap: 0.55rem;
  margin: 0.75rem 0 0;
  padding: 0;
  list-style: none;
}

.documentation-steps li {
  display: flex;
  gap: 0.55rem;
  align-items: flex-start;
  color: #625e70;
  font-size: 0.8rem;
  line-height: 1.45;
}

.documentation-steps li span {
  display: inline-flex;
  width: 1.35rem;
  height: 1.35rem;
  flex: 0 0 1.35rem;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: #7367f0;
  border-radius: 50%;
  font-size: 0.68rem;
  font-weight: 700;
}

.documentation-footer {
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid #ebe9f1;
}

.documentation-footer > span {
  display: inline-flex;
  gap: 0.35rem;
  align-items: flex-start;
  color: #8f8b9f;
  font-size: 0.72rem;
}

@media (max-width: 992px) {
  .configuration-layout,
  .field-grid,
  .quick-field-grid {
    grid-template-columns: 1fr;
  }

  .page-heading,
  .editor-header,
  .list-header,
  .template-header {
    align-items: stretch;
    flex-direction: column;
  }

  .template-select {
    max-width: none;
  }

  .page-heading-actions {
    justify-content: flex-start;
  }

  .company-list-card {
    position: static;
    max-height: none;
  }

  .list-toolbar-buttons,
  .editor-actions {
    width: 100%;
  }

  .list-toolbar-buttons .new-button,
  .editor-actions > * {
    flex: 1;
  }

  .editor-title {
    max-width: none;
  }

  .documentation-fields {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 576px) {
  .page-hero {
    padding: 1rem;
  }

  .page-heading-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    width: 100%;
  }

  .refresh-button {
    grid-column: 1 / -1;
    width: 100%;
  }

  .page-title {
    font-size: 1.35rem;
  }

  .list-toolbar-buttons {
    flex-direction: column;
  }

  .company-row {
    align-items: flex-start;
  }

  .company-status {
    font-size: 0.65rem;
  }

  .documentation-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .documentation-footer .btn {
    width: 100%;
  }
}
</style>

<style>
.integration-documentation-modal .modal-dialog {
  max-width: 760px;
}

.integration-documentation-modal .modal-content {
  overflow: hidden;
  border: 0;
  border-radius: 14px;
  box-shadow: 0 18px 50px rgba(47, 43, 61, 0.2);
}

.integration-documentation-modal .modal-header {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #ebe9f1;
}

.integration-documentation-modal .modal-body {
  max-height: 72vh;
  padding: 1.25rem;
  overflow-y: auto;
}
</style>
